// Page lifecycle and robustness (IMPLEMENTATION §0.1 "Transient state", §2.9 "Save", §3 M11).
//
// installLifecycle — every autosave trigger (`beforeunload` is never used):
//   every `autosaveMs`             a save ('timer'; null = saving off: ?test / ?scenario unless ?autosave=1)
//   economic events                a save `debounceMs` after the LAST buy / build / expand / sell event
//                                  ('debounce'; ui.json save.debounceSec; a newer event restarts the wait)
//   visibilitychange → hidden      release + holdEnd (the controller), mute audio, then a save ('hidden')
//   visibilitychange → visible     unmute, restart the loop clock, `onResume` (the stale-owner check)
//   pagehide                       release + holdEnd, then a save ('pagehide'; a reload / tab close / bfcache)
//   pageshow (persisted)           back from the bfcache: restart the loop clock, `onRestore` (re-claim)
//   freeze / resume                a save ('freeze'; Chrome's Page Lifecycle) / restart the loop clock,
//                                  `onResume`
//   flush(reason)                  a save now: WebGL context loss, the lock handover, before a reload
//   stopSaving()                   for good (this instance lost the single-instance lock): no timer, no
//                                  debounce, no trigger ever saves again, and the audio stays muted
//   holdAudio()                    the audio stays muted for good, saving goes on (a blocking overlay while
//                                  the sim runs: the ContextLostOverlay)
// Any save clears a pending debounce. With saving off, only the hide handling runs.
//
// installRobustness — the M11 wiring around it (main.ts calls it once the game runs):
//   single-instance lock lost  → the `lock_lost` row, then flush (the owner's last save, only while storage
//                                still says it owns the save; the lock posts `yield` after it — the row
//                                rides in that last telemetry write), stop saving (+ SaveService.disable),
//                                stop the loop, mute, ElsewhereOverlay ("Play here" reloads: the reloaded
//                                page claims as the newest)
//   stale owner                → the same without a flush. Before EVERY save the lock's storage mark must
//                                name this instance (`mayWrite` = the lifecycle's `canSave`); SaveService
//                                refuses a write when another instance wrote the main since (compare-and-
//                                swap → `onConflict`); visible / Page Lifecycle resume check both
//                                (`onResume`). So a frozen / suspended owner that missed a newer claim (the
//                                message dropped, or heard only after the new owner played) never writes.
//   webglcontextlost           → flush, hold the audio (the sim keeps running behind the overlay: nothing
//                                may sound), ContextLostOverlay ("Tap to reload" flushes again and reloads)
//   bfcache restore            → re-claim the lock; if another instance saved meanwhile, stop saving (the
//                                stale state is never written) and reload
//   navigator.storage.persist() only in a secure context (platform/storage.ts) and only for a saving owner
import type { InstanceLock, LockState } from '../platform/instanceLock.ts';
import type { PersistResult } from '../platform/storage.ts';
import type { SimEvent } from '../sim/events.ts';

export type SaveReason =
  | 'timer'
  | 'debounce'
  | 'hidden'
  | 'pagehide'
  | 'freeze'
  | 'contextlost'
  | 'handover'
  | 'reload';

export const SAVE_REASONS: readonly SaveReason[] = [
  'timer',
  'debounce',
  'hidden',
  'pagehide',
  'freeze',
  'contextlost',
  'handover',
  'reload',
];

/** Events that schedule the debounced save: buy, build, expand, sell (and their undos). */
export const ECONOMIC_EVENTS: readonly SimEvent['e'][] = [
  'place',
  'built',
  'bmerge',
  'bremove',
  'expand',
  'sell',
  'saleUndone',
];

/** Optional audio mute sink (M9's SfxEngine / the M0 unlock context); muted while hidden or inactive. */
export interface AudioMute {
  setMuted(muted: boolean): void;
}

export interface LifecycleDeps {
  win: Window;
  /** Autosave period in ms; null disables saving (the hide handling still runs). */
  autosaveMs: number | null;
  /** Debounce after an economic event (ui.json save.debounceSec × 1000). */
  debounceMs: number;
  /** Page hidden / pagehide: the controller dispatches release + holdEnd. */
  onHidden(): void;
  /** Page visible again (visibilitychange, bfcache restore, Page Lifecycle resume): restart the loop clock. */
  onShown?(): void;
  /** Visible again or Page Lifecycle resume (not a bfcache restore: onRestore), after onShown. */
  onResume?(): void;
  /** Back from the bfcache (pageshow persisted), after onShown. */
  onRestore?(): void;
  save(reason: SaveReason): void;
  /**
   * Extra gate checked before every save (installRobustness.mayWrite: false while a re-claim of the lock
   * is pending, and for good once another instance owns the save).
   */
  canSave?(): boolean;
  audio?: AudioMute | null;
}

export interface Lifecycle {
  /** The controller's fan-out: an economic event (re)starts the debounced save. */
  onEvents(events: readonly SimEvent[]): void;
  /** Saves now (no-op when saving is off or stopped). */
  flush(reason: SaveReason): void;
  /** Stops every save for good and keeps the audio muted (single-instance lock lost). */
  stopSaving(): void;
  /** Keeps the audio muted for good (a blocking overlay over a running sim: context lost); saves go on. */
  holdAudio(): void;
  readonly saving: boolean;
  /** Saves made per reason since install (diagnostics: __gm.lifecycle). */
  counts(): Record<SaveReason, number>;
  dispose(): void;
}

type Listener = [target: EventTarget, type: string, fn: (ev: Event) => void];

export function installLifecycle(d: LifecycleDeps): Lifecycle {
  let saving = d.autosaveMs !== null;
  let inactive = false;
  let debounce: number | null = null;
  const counts = Object.fromEntries(SAVE_REASONS.map((r) => [r, 0])) as Record<SaveReason, number>;
  const economic: readonly string[] = ECONOMIC_EVENTS;

  const clearDebounce = (): void => {
    if (debounce !== null) d.win.clearTimeout(debounce);
    debounce = null;
  };
  const save = (reason: SaveReason): void => {
    if (!saving || d.canSave?.() === false) return;
    clearDebounce();
    counts[reason] += 1;
    d.save(reason);
  };
  const mute = (m: boolean): void => {
    d.audio?.setMuted(m || inactive);
  };
  const hide = (reason: 'hidden' | 'pagehide'): void => {
    d.onHidden();
    mute(true);
    save(reason);
  };
  const shown = (): void => {
    mute(false);
    d.onShown?.();
  };

  const doc = d.win.document;
  const listeners: Listener[] = [
    [
      doc,
      'visibilitychange',
      () => {
        if (doc.visibilityState === 'hidden') hide('hidden');
        else {
          shown();
          d.onResume?.();
        }
      },
    ],
    [d.win, 'pagehide', () => hide('pagehide')],
    [
      d.win,
      'pageshow',
      (ev) => {
        if ((ev as PageTransitionEvent).persisted !== true) return;
        shown();
        d.onRestore?.();
      },
    ],
    [doc, 'freeze', () => save('freeze')],
    [
      doc,
      'resume',
      () => {
        d.onShown?.();
        d.onResume?.();
      },
    ],
  ];
  for (const [t, type, fn] of listeners) t.addEventListener(type, fn);
  let timer: number | null =
    d.autosaveMs !== null ? d.win.setInterval(() => save('timer'), d.autosaveMs) : null;

  const holdAudio = (): void => {
    inactive = true;
    d.audio?.setMuted(true);
  };
  const stopTimers = (): void => {
    clearDebounce();
    if (timer !== null) d.win.clearInterval(timer);
    timer = null;
  };

  return {
    onEvents(events) {
      if (!saving) return;
      if (!events.some((e) => economic.includes(e.e))) return;
      clearDebounce();
      debounce = d.win.setTimeout(() => {
        debounce = null;
        save('debounce');
      }, d.debounceMs);
    },
    flush: save,
    stopSaving() {
      saving = false;
      stopTimers();
      holdAudio();
    },
    holdAudio,
    get saving() {
      return saving;
    },
    counts: () => ({ ...counts }),
    dispose() {
      stopTimers();
      for (const [t, type, fn] of listeners) t.removeEventListener(type, fn);
    },
  };
}

// ---- the save step (main.ts's `save` callback) ------------------------------------------------------------

/** What SaveService.save reports (save/SaveService.ts SaveWrite). */
export interface SaveOutcome {
  ok: boolean;
  /** Nothing written: the service is disabled (lock lost). */
  blocked?: boolean;
  /** Nothing written: another instance wrote the main since (compare-and-swap). */
  conflict?: boolean;
}

export interface SaveStepDeps {
  /** A frame threw: the game state is not saved (the last good save stays), telemetry still is. */
  halted(): boolean;
  /** The game save (SaveService.save of the running state). */
  write(): SaveOutcome;
  /** The M12 telemetry hub (debug/telemetryHub.ts). */
  telemetry: { onSave(reason: SaveReason): void; onSaveFailed(reason: SaveReason): void };
  /** installRobustness.onConflict: this instance is stale — out, no write. */
  onConflict(): void;
  warn(message: string): void;
}

/**
 * One lifecycle save: the game save, then the telemetry rows and at most ONE telemetry write (M12 #12).
 *   conflict  another instance wrote the main: out (onConflict); the ring is shared storage it owns now —
 *             not written either
 *   blocked   SaveService disabled (lock lost): nothing is written, ever
 *   failed    storage full (or blocked): the stored telemetry ring gives its space back (`onSaveFailed`:
 *             an error row, the rows stay in memory and a later flush rewrites them), ONE retry of the game
 *             save, and no telemetry write this time — so the ring can never keep the game save out. When the
 *             retry fails too, the ring was not the obstacle (blocked storage, a main too large on its own):
 *             the telemetry step runs as usual and writes the ring back (if it fits)
 *   halted    no game save; the telemetry still flushes (the halt's error rows)
 */
export function saveGameThenTelemetry(d: SaveStepDeps, reason: SaveReason): void {
  if (!d.halted()) {
    const res = d.write();
    if (res.conflict) {
      d.onConflict();
      return;
    }
    if (res.blocked) return;
    if (!res.ok) {
      d.warn('[save] write failed (storage full or blocked)');
      d.telemetry.onSaveFailed(reason);
      const again = d.write();
      if (again.conflict) {
        d.onConflict();
        return;
      }
      if (again.ok || again.blocked) return;
      d.warn('[save] retry failed too');
    }
  }
  d.telemetry.onSave(reason);
}

// ---- robustness wiring --------------------------------------------------------------------------------

/** The overlays M11 shows (ui/overlays: small standalone components; M7 builds the full shell). */
export interface OverlaySink {
  contextLost(onTap: () => void): void;
  elsewhere(onPlayHere: () => void): void;
}

export interface TelemetryLog {
  log(row: { event: string; subject: string; value: number; extra: string }): void;
}

export interface RobustnessDeps {
  /** null when this instance does not save (it then never takes part in the lock). */
  lock: Pick<InstanceLock, 'state' | 'claim' | 'holdsMark' | 'abandon'> | null;
  life: Lifecycle;
  saves: { disable(): void; mainChanged(): boolean };
  game: { setSpeed(k: number): void };
  overlays: OverlaySink;
  /** The game canvas (null with ?render=none). */
  canvas: HTMLCanvasElement | null;
  /** Late binding for the lock's onLost (the lock was created before the game existed). */
  bindLockLost(fn: (wasOwner: boolean) => void): void;
  watchContextLoss(canvas: HTMLCanvasElement, h: { onLost(): void }): () => void;
  /** navigator.storage.persist() (platform/storage.ts: secure contexts only). */
  persist(): Promise<PersistResult>;
  telemetry: TelemetryLog;
  reload(): void;
}

export interface Robustness {
  /** 'off' when this instance does not save. */
  lockState(): LockState | 'off';
  contextLost(): boolean;
  persist(): PersistResult | 'pending' | 'off';
  /** Bfcache restores handled (pageshow persisted). */
  restores(): number;
  /** The lifecycle's pageshow-persisted hook: re-claim the lock, reload when another instance saved. */
  onRestore(): void;
  /**
   * The lifecycle's save gate (`canSave`): false while a (re-)claim is pending or once this instance is
   * out; a storage mark naming another owner puts this stale instance out on the spot (no write).
   */
  mayWrite(): boolean;
  /** SaveService refused a write (compare-and-swap: another instance wrote the main since): out, no write. */
  onConflict(): void;
  /** The lifecycle's visible / resume hook: a stale owner (another mark or another main) goes out. */
  onResume(): void;
  dispose(): void;
}

export function installRobustness(d: RobustnessDeps): Robustness {
  let lostCtx = false;
  let persist: PersistResult | 'pending' | 'off' = 'off';
  let restores = 0;
  /** Out for good (lock lost, stale, or reloading into another instance's save): never writes again. */
  let out = false;
  /** The owner's last (handover) flush is running: the only save allowed once the lock says 'lost'. */
  let handover = false;
  /** The one `lock_lost` row was logged (a handover flush that meets a conflict goes out a second way). */
  let lostLogged = false;
  const reload = (): void => {
    d.life.flush('reload');
    d.reload();
  };

  /** Storage says another instance owns the save now (its mark, or a main this one did not write). */
  const stale = (): boolean => d.lock !== null && (!d.lock.holdsMark() || d.saves.mainChanged());

  const goOut = (subject: 'owner' | 'claiming' | 'stale', flush: boolean): void => {
    if (out) return;
    // the row FIRST: the handover flush below is this ring's last write (saving stops right after it); on
    // the stale / claiming paths no write follows — this instance may not touch the shared ring any more
    if (!lostLogged) {
      lostLogged = true;
      d.telemetry.log({ event: 'lock_lost', subject, value: 0, extra: '' });
    }
    if (flush) {
      handover = true;
      try {
        d.life.flush('handover');
      } finally {
        handover = false;
      }
      if (out) return; // the flush met a conflict (onConflict) and already put this instance out
    }
    out = true;
    d.lock?.abandon(); // a stale owner that never heard the claim (no-op once the lock has lost)
    d.life.stopSaving();
    d.saves.disable();
    d.game.setSpeed(0);
    d.overlays.elsewhere(() => d.reload());
  };

  // the owner flushes its last save for the claimer — unless the claim came late and a newer instance
  // already owns (and maybe wrote) the save: then this state is stale and must not land
  const onLockLost = (wasOwner: boolean): void => {
    goOut(wasOwner ? 'owner' : 'claiming', wasOwner && !stale());
  };
  d.bindLockLost(onLockLost);
  if (d.lock?.state === 'lost') onLockLost(false); // a newer instance claimed while this one booted

  const unwatch = d.canvas
    ? d.watchContextLoss(d.canvas, {
        onLost() {
          lostCtx = true;
          d.life.flush('contextlost');
          d.life.holdAudio(); // the sim runs on behind the opaque overlay: gallery / coins must not sound
          d.telemetry.log({ event: 'context_lost', subject: 'webgl', value: 0, extra: '' });
          d.overlays.contextLost(reload);
        },
      })
    : () => undefined;

  if (d.lock?.state === 'owner') {
    persist = 'pending';
    void d.persist().then((r) => {
      persist = r;
      d.telemetry.log({
        event: 'storage_persist',
        subject: r,
        value: r === 'granted' ? 1 : 0,
        extra: '',
      });
    });
  }

  return {
    lockState: () => (d.lock ? d.lock.state : 'off'),
    contextLost: () => lostCtx,
    persist: () => persist,
    restores: () => restores,
    onRestore() {
      restores += 1;
      const lock = d.lock;
      if (!lock || out || lock.state === 'lost') return;
      void lock.claim().then((st) => {
        if (out || st !== 'owner' || !d.saves.mainChanged()) return;
        // another instance saved while this page sat in the bfcache: continue from ITS save — and never
        // write this page's stale state on the way out (the reload's pagehide would)
        out = true;
        d.life.stopSaving();
        d.saves.disable();
        d.reload();
      });
    },
    mayWrite() {
      const lock = d.lock;
      if (lock === null || handover) return true;
      if (out || lock.state === 'lost' || lock.state === 'claiming') return false;
      if (lock.holdsMark()) return true; // SaveService compares the main itself (onConflict)
      goOut('stale', false);
      return false;
    },
    onConflict() {
      goOut('stale', false);
    },
    onResume() {
      const lock = d.lock;
      if (lock === null || out || lock.state !== 'owner') return;
      if (stale()) goOut('stale', false);
    },
    dispose() {
      unwatch();
    },
  };
}
